import { useState } from 'react'
import { AlertTriangle, Check, ChevronDown, Clock, Ticket, X } from 'lucide-react'
import { EmptyState } from '@/components/EmptyState'
import { ErrorState } from '@/components/ErrorState'
import { SkeletonCard } from '@/components/LoadingSkeleton'
import { TeamCrest } from '@/components/TeamCrest'
import { useQuinielaTickets } from '@/hooks/useQuiniela'
import { cn, formatKickoff } from '@/lib/utils'
import { formatEur, formatOneIn, kindOf } from '@/lib/quiniela'
import type { QuinielaTicket, TicketMatchStatus } from '@/types/quiniela'
import { KIND_STYLE } from './MatchProbRow'

const CATEGORY_TONE: Record<string, string> = {
  especial: 'border-gold/50 bg-gold/10 text-gold',
  '1': 'border-brand/50 bg-brand-soft/40 text-brand',
  '2': 'border-brand/40 bg-brand-soft/30 text-brand',
  '3': 'border-brand/40 bg-brand-soft/30 text-brand',
  '4': 'border-brand/40 bg-brand-soft/30 text-brand',
  '5': 'border-brand/40 bg-brand-soft/30 text-brand',
  none: 'border-border bg-panel-elevated text-text-muted',
}

function MatchStatusIcon({ status }: { status: TicketMatchStatus }) {
  if (status === 'hit') return <span className="h-5 w-5 rounded-full bg-brand text-black flex items-center justify-center" aria-label="Acierto"><Check className="h-3 w-3" strokeWidth={3} /></span>
  if (status === 'miss') return <span className="h-5 w-5 rounded-full bg-danger/20 text-danger flex items-center justify-center" aria-label="Fallo"><X className="h-3 w-3" strokeWidth={3} /></span>
  if (status === 'warning') return <span className="h-5 w-5 rounded-full bg-warn/20 text-warn flex items-center justify-center" aria-label="Pendiente de regla oficial"><AlertTriangle className="h-3 w-3" /></span>
  return <span className="h-5 w-5 rounded-full border border-border text-text-dim flex items-center justify-center" aria-label="Pendiente"><Clock className="h-3 w-3" /></span>
}

function TicketDetail({ t }: { t: QuinielaTicket }) {
  const tr = t.tracking
  if (!tr) return null
  const dist = t.columns_by_hits ?? tr.columns_by_hits
  return (
    <div className="space-y-3 pt-3 border-t border-border/60">
      {tr.notice && (
        <p className="text-[11px] text-warn flex items-start gap-1.5 leading-snug">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-px" /> <span>{tr.notice}</span>
        </p>
      )}

      <div className="-mx-1">
        {tr.matches.map((m) => {
          const kind = kindOf(m.signs.length) ?? 'simple'
          const score = m.home_goals != null && m.away_goals != null ? `${m.home_goals}–${m.away_goals}` : null
          return (
            <div key={m.position} className="flex items-center gap-2 px-1 py-1.5 border-b border-border/40 last:border-b-0" data-testid={`ticket-match-${m.position}`}>
              <span className="figure w-4 text-right text-[11px] font-bold text-text-dim">{m.position}</span>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5 min-w-0">
                  <TeamCrest src={m.home_logo} alt={m.home_name ?? ''} size={14} />
                  <span className="truncate text-[12px] font-semibold text-text">{m.home_name}</span>
                </div>
                <div className="flex items-center gap-1.5 min-w-0">
                  <TeamCrest src={m.away_logo} alt={m.away_name ?? ''} size={14} />
                  <span className="truncate text-[11px] text-text-muted">{m.away_name}</span>
                </div>
              </div>
              <span className={cn('figure rounded-md border px-1.5 py-0.5 text-[11px] font-black', KIND_STYLE[kind].chip)}>{m.signs.join('')}</span>
              <span className="figure w-[3.4rem] text-center text-[11px] text-text-muted">
                {score ? <>{score}<span className="block text-[9px] text-text-dim">{m.actual_sign}</span></> : '—'}
              </span>
              <MatchStatusIcon status={m.status} />
            </div>
          )
        })}
      </div>

      <div className="rounded-xl border border-gold/35 bg-gold/[0.06] p-2.5 flex items-center justify-between gap-2" data-testid="ticket-pleno">
        <div className="min-w-0">
          <p className="text-[10px] font-black uppercase tracking-wider text-gold">Pleno al 15</p>
          <p className="truncate text-[11px] text-text-muted">{tr.pleno.home_name} – {tr.pleno.away_name}</p>
        </div>
        <div className="text-right shrink-0">
          <p className="figure text-sm font-black text-gold">{t.pleno.home} – {t.pleno.away}</p>
          <p className="text-[10px] text-text-dim">
            {tr.pleno.actual ? `Real ${tr.pleno.actual.home} – ${tr.pleno.actual.away}` : 'Pendiente'}
          </p>
        </div>
        <MatchStatusIcon status={tr.pleno.status} />
      </div>

      {dist && (
        <div className="space-y-1.5" data-testid="ticket-columns-by-hits">
          <p className="text-[10px] font-semibold text-text-dim">Apuestas del boleto por aciertos</p>
          <div className="grid grid-cols-5 gap-1.5">
            {['14', '13', '12', '11', '10'].map((h) => (
              <div key={h} className={cn('rounded-lg border px-1 py-1.5 text-center', (dist[h] ?? 0) > 0 ? 'border-brand/40 bg-brand-soft/30' : 'border-border bg-panel-elevated')}>
                <p className="text-[9px] text-text-dim leading-none">{h} aciertos</p>
                <p className={cn('figure text-sm font-black mt-1 leading-none', (dist[h] ?? 0) > 0 ? 'text-brand' : 'text-text-muted')}>{dist[h] ?? 0}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-[10px] text-text-dim">
        <dt>Jugado</dt><dd className="figure text-right text-text-muted">{formatKickoff(t.played_at)}</dd>
        <dt>Análisis de JA usado</dt><dd className="figure text-right text-text-muted">versión {t.snapshot_version}</dd>
        <dt>Probabilidad estimada</dt><dd className="figure text-right text-text-muted">{formatOneIn(t.estimated_probability)}</dd>
        <dt>Mi Banca</dt><dd className="text-right text-text-muted">{t.stake_deducted ? 'Coste descontado' : 'No aplicado'}</dd>
      </dl>
    </div>
  )
}

function TicketCard({ t }: { t: QuinielaTicket }) {
  const [open, setOpen] = useState(false)
  const tr = t.tracking
  const settled = t.status === 'settled'
  const hits = settled ? t.hits ?? 0 : tr?.hits ?? 0
  const catTone = t.category ? CATEGORY_TONE[t.category] : null

  return (
    <article className="card !p-3.5 space-y-3" data-testid={`ticket-${t.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[13px] font-bold text-text">Jornada {t.jornada_number}</p>
          <p className="text-[10px] text-text-dim mt-0.5">
            {t.origin === 'ja' ? 'Creada con JA' : 'Creada por ti'} · {formatKickoff(t.played_at)}
          </p>
        </div>
        <span className={cn('shrink-0 rounded-full border px-2.5 py-0.5 text-[10px] font-black uppercase tracking-wider',
          settled ? 'border-brand/40 bg-brand-soft/30 text-brand' : 'border-border bg-panel-elevated text-text-muted')}>
          {settled ? 'Liquidado' : 'Pendiente'}
        </span>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div>
          <p className="text-[10px] font-semibold text-text-dim">Aciertos</p>
          <p className="figure text-[1.6rem] font-black leading-none text-text mt-1" data-testid={`ticket-hits-${t.id}`}>
            {hits}<span className="text-sm text-text-dim">/14</span>
          </p>
          {!settled && tr && tr.pending > 0 && <p className="text-[9px] text-text-dim mt-1">{tr.pending} por jugar</p>}
        </div>
        <div>
          <p className="text-[10px] font-semibold text-text-dim">Coste</p>
          <p className="figure text-lg font-black leading-none text-text mt-1.5">{formatEur(t.cost_eur)}</p>
          <p className="text-[9px] text-text-dim mt-1">{t.columns} apuestas</p>
        </div>
        <div>
          <p className="text-[10px] font-semibold text-text-dim">Pleno</p>
          <p className="figure text-lg font-black leading-none text-gold mt-1.5">{t.pleno.home}–{t.pleno.away}</p>
          <p className="text-[9px] text-text-dim mt-1">
            {tr?.pleno.status === 'hit' ? 'Acertado' : tr?.pleno.status === 'miss' ? 'Fallado' : 'Pendiente'}
          </p>
        </div>
      </div>

      {settled && t.category_label && catTone && (
        <div className={cn('rounded-lg border px-3 py-2 flex items-center justify-between', catTone)}>
          <span className="text-[12px] font-black">{t.category_label}</span>
          <span className="text-[10px] opacity-80">{t.hits} aciertos{t.pleno_hit ? ' + Pleno' : ''}</span>
        </div>
      )}
      {tr?.notice && !open && (
        <p className="text-[10px] text-warn flex items-start gap-1.5 leading-snug">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-px" /> <span>Pendiente de la regla oficial de SELAE para partidos aplazados.</span>
        </p>
      )}

      <button
        type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open}
        className="w-full flex items-center justify-center gap-1.5 text-[11px] font-bold text-text-muted hover:text-text"
      >
        {open ? 'Ocultar detalle' : 'Ver detalle'} <ChevronDown className={cn('h-3.5 w-3.5 transition-transform', open && 'rotate-180')} />
      </button>
      {open && <TicketDetail t={t} />}
    </article>
  )
}

/** Historial → Mis apuestas → Mis boletos. Solo boletos de La Quiniela (tabla propia): nunca se mezclan con las
 * apuestas normales ni cuentan en su win rate/ROI/cuota media. */
export function MyTickets() {
  const { data, isLoading, isError, error, refetch } = useQuinielaTickets(true)

  if (isLoading) return <div className="space-y-3"><SkeletonCard /><SkeletonCard /></div>
  if (isError || !data) return <ErrorState title="No pudimos cargar tus boletos" error={error} onRetry={() => refetch()} />
  if (data.items.length === 0) {
    return (
      <EmptyState
        icon={Ticket} title="Todavía no has jugado ningún boleto"
        body="Cuando juegues una Quiniela con LA JUGUÉ, la verás aquí con sus aciertos y su liquidación."
        cta={{ label: 'Ir a La Quiniela', to: '/quiniela' }}
      />
    )
  }
  const { summary } = data
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between px-0.5 text-[11px] text-text-dim">
        <span><span className="figure font-bold text-text">{summary.total}</span> {summary.total === 1 ? 'boleto' : 'boletos'} · <span className="figure">{summary.pending}</span> pendientes</span>
        <span>Coste total <span className="figure font-bold text-text">{formatEur(summary.total_cost_eur)}</span></span>
      </div>
      {data.items.map((t) => <TicketCard key={t.id} t={t} />)}
    </div>
  )
}
